import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import moment, { Moment } from 'moment'
import i18next from 'i18next'
import {
	Alert,
	Card,
	Col,
	DatePicker,
	Empty,
	Row,
	Select,
	Spin,
	Statistic,
	Table,
	Tag,
	Tooltip
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import {
	Bar,
	CartesianGrid,
	ComposedChart,
	Legend,
	Line,
	ResponsiveContainer,
	XAxis,
	YAxis
} from 'recharts'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState } from '../../../reducers'
import queryKeys from '../../../queries/queryKeys'
import {
	fetchJobsLifecyclePerMonthStats,
	IJobsLifecyclePerMonthItem
} from '../../../queries/statistics/fetchStatistics'

const DEFAULT_MONTHS_BACK = 12
const MAX_MONTHS_BACK = 120

const colorByDelta = (delta: number) => {
	if (delta > 0) {
		return '#389e0d'
	}
	if (delta < 0) {
		return '#cf1322'
	}
	return '#8c8c8c'
}

const disabledUnavailableMonth = (current: Moment) => {
	const maxMonth = moment().endOf('month')
	const minMonth = moment().startOf('month').subtract(MAX_MONTHS_BACK - 1, 'months')
	return current && (current > maxMonth || current < minMonth)
}

const MyOrganizationJobsLifecycle = () => {
	const { t } = useTranslation('pages')

	const organizations = useSelector((state: RootState) => {
		const profile = state.profile.profile.data
		const allOrganizations = [profile?.currentOrganization, ...(profile?.availableOrganizations || [])]
			.filter((org): org is { id: number; name: string } => Boolean(org?.id && org?.name))

		return allOrganizations.filter((organization, index, source) => (
			index === source.findIndex((item) => item.id === organization.id)
		))
	})

	const defaultOrganizationIds = useMemo(() => organizations.map((organization) => organization.id), [organizations])
	const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<number[]>(defaultOrganizationIds)
	const [startMonth, setStartMonth] = useState<Moment>(moment().startOf('month').subtract(DEFAULT_MONTHS_BACK - 1, 'months'))

	useEffect(() => {
		if (!selectedOrganizationIds.length && defaultOrganizationIds.length) {
			setSelectedOrganizationIds(defaultOrganizationIds)
		}
	}, [defaultOrganizationIds, selectedOrganizationIds.length])

	const monthsBack = useMemo(() => (
		Math.min(
			Math.max(moment().startOf('month').diff(startMonth.clone().startOf('month'), 'months') + 1, 1),
			MAX_MONTHS_BACK
		)
	), [startMonth])

	const { data, isLoading } = useQuery({
		queryKey: queryKeys.statistics.jobsLifecyclePerMonth({
			organizationIds: selectedOrganizationIds,
			monthsBack
		}),
		queryFn: () => fetchJobsLifecyclePerMonthStats({
			organizationIds: selectedOrganizationIds,
			monthsBack
		}),
		enabled: selectedOrganizationIds.length > 0
	})

	const tableData = data?.jobsLifecyclePerMonth || []
	const chartData = [...tableData].reverse().map((item) => ({
		...item,
		monthLabel: moment(item.month, 'YYYY-MM').format('MM.YYYY'),
		netDelta: item.createdJobs - item.ceasedJobs
	}))
	const totalCreated = tableData.reduce((sum, row) => sum + row.createdJobs, 0)
	const totalCeased = tableData.reduce((sum, row) => sum + row.ceasedJobs, 0)
	const totalNetDelta = totalCreated - totalCeased

	const renderCreatedJobs = (value: number, record: IJobsLifecyclePerMonthItem) => (
		<Tooltip title={(record.createdDetails || []).slice(0, 8).map((item) => item.address).join(', ') || '-'}>
			<Tag color={'green'}>{value}</Tag>
		</Tooltip>
	)

	const renderCeasedJobs = (value: number, record: IJobsLifecyclePerMonthItem) => (
		<Tooltip title={(record.ceasedDetails || []).slice(0, 8).map((item) => item.address).join(', ') || '-'}>
			<Tag color={'red'}>{value}</Tag>
		</Tooltip>
	)

	const renderBalance = (_: unknown, record: IJobsLifecyclePerMonthItem) => {
		const delta = record.createdJobs - record.ceasedJobs
		return <Tag color={colorByDelta(delta)}>{delta > 0 ? `+${delta}` : delta}</Tag>
	}

	const columns: ColumnsType<IJobsLifecyclePerMonthItem> = [
		{
			title: i18next.t('pages:Mesiac'),
			dataIndex: 'month',
			key: 'month',
			width: 120,
			render: (value: string) => moment(value, 'YYYY-MM').format('MM.YYYY')
		},
		{
			title: i18next.t('pages:Aktívne zákazky (koniec mesiaca)'),
			dataIndex: 'totalActiveJobsMonthEnd',
			key: 'totalActiveJobsMonthEnd',
			width: 220
		},
		{
			title: i18next.t('pages:Nové zákazky'),
			dataIndex: 'createdJobs',
			key: 'createdJobs',
			width: 180,
			render: renderCreatedJobs
		},
		{
			title: i18next.t('pages:Ukončené zákazky'),
			dataIndex: 'ceasedJobs',
			key: 'ceasedJobs',
			width: 180,
			render: renderCeasedJobs
		},
		{
			title: i18next.t('pages:Bilancia'),
			key: 'balance',
			width: 150,
			render: renderBalance
		}
	]

	return (
		<Card className={'my-4'} bordered={false}>
			<Row gutter={[16, 16]} align={'middle'} justify={'space-between'}>
				<Col>
					<div className={'font-bold text-lg'}>{t('pages:Životný cyklus zákaziek')}</div>
					<div className={'text-gray-500'}>{t('pages:Manažérsky prehľad: vznik, útlm a aktívny stav zákaziek')}</div>
				</Col>
			</Row>

			<Row gutter={[16, 16]} className={'mt-4'}>
				<Col xs={24} md={16} lg={14}>
					<label className={'block mb-1 font-semibold'}>{t('pages:Organizácie')}</label>
					<Select
						mode={'multiple'}
						value={selectedOrganizationIds}
						onChange={(value) => setSelectedOrganizationIds(value)}
						style={{ width: '100%' }}
						placeholder={t('pages:Vyber organizácie')}
						maxTagCount={4}
						allowClear={false}
					>
						{
							organizations.map((organization) => (
								<Select.Option key={organization.id} value={organization.id}>
									{organization.name}
								</Select.Option>
							))
						}
					</Select>
				</Col>
				<Col xs={24} md={8} lg={6}>
					<label className={'block mb-1 font-semibold'}>{t('pages:Štatistika od mesiaca')}</label>
					<DatePicker
						picker={'month'}
						allowClear={false}
						value={startMonth as any}
						onChange={(value) => {
							if (value) {
								setStartMonth(moment(value).startOf('month'))
							}
						}}
						disabledDate={disabledUnavailableMonth}
						style={{ width: '100%' }}
					/>
				</Col>
			</Row>

			{
				selectedOrganizationIds.length === 0 ? (
					<Alert
						className={'mt-4'}
						type={'info'}
						showIcon
						message={t('pages:Vyber aspoň jednu organizáciu pre zobrazenie štatistiky')}
					/>
				) : (
					<Spin spinning={isLoading}>
						<Row gutter={[16, 16]} className={'mt-4'}>
							<Col xs={24} md={8}>
								<Card size={'small'}>
									<Statistic
										title={t('pages:Aktívne zákazky aktuálne')}
										value={data?.context?.totalActiveJobsNow || 0}
									/>
								</Card>
							</Col>
							<Col xs={24} md={8}>
								<Card size={'small'}>
									<Statistic
										title={t('pages:Nové zákazky v období')}
										value={totalCreated}
									/>
								</Card>
							</Col>
							<Col xs={24} md={8}>
								<Card size={'small'}>
									<Statistic
										title={t('pages:Čistá bilancia')}
										value={totalNetDelta}
										valueStyle={{ color: colorByDelta(totalNetDelta) }}
										prefix={totalNetDelta > 0 ? '+' : ''}
									/>
								</Card>
							</Col>
						</Row>

						<Card size={'small'} className={'mt-4'}>
							{
								chartData.length > 0 ? (
									<div style={{ height: 360 }}>
										<ResponsiveContainer width={'100%'} height={'100%'}>
											<ComposedChart
												data={chartData}
												margin={{
													top: 10,
													right: 20,
													left: 0,
													bottom: 10
												}}
											>
												<CartesianGrid strokeDasharray="4 4" />
												<XAxis dataKey={'monthLabel'} />
												<YAxis />
												<Legend />
												<Bar dataKey={'createdJobs'} name={t('pages:Nové')} fill={'#52c41a'} radius={[4, 4, 0, 0]} />
												<Bar dataKey={'ceasedJobs'} name={t('pages:Ukončené')} fill={'#ff4d4f'} radius={[4, 4, 0, 0]} />
												<Line
													type={'monotone'}
													dataKey={'totalActiveJobsMonthEnd'}
													name={t('pages:Aktívne ku koncu mesiaca')}
													stroke={'#1890ff'}
													strokeWidth={2}
													dot={{ r: 3 }}
												/>
											</ComposedChart>
										</ResponsiveContainer>
									</div>
								) : (
									<Empty description={t('pages:Žiadne dáta')} />
								)
							}
						</Card>

						<Table
							className={'general-table mt-4'}
							rowKey={(row) => row.month}
							columns={columns}
							dataSource={tableData}
							pagination={false}
							size={'small'}
							locale={{
								emptyText: <Empty description={t('pages:Žiadne dáta')} />
							}}
						/>
					</Spin>
				)
			}
		</Card>
	)
}

export default MyOrganizationJobsLifecycle
